import {useEffect, useMemo, useState} from 'react';

import {safeBlob, safeContentType} from '@embertracking/common/data/fileTypes.mjs';

import {whenAccessLeaseReady} from './sessionAccess';

const DEFAULT_ENTRY_BYTES = 16 * 1024 * 1024;
const DEFAULT_TOTAL_BYTES = 64 * 1024 * 1024;
const DEFAULT_ENTRIES = 64;
const DEFAULT_TTL_MS = 5 * 60 * 1000;
const STREAM_PATH = '/download/';
const TOKEN_EXPIRY_SKEW_MS = 30 * 1000;

const errorWithCode = (message, code, status) => {
    const error = new Error(message);
    error.code = code;
    if (status) error.status = status;
    return error;
};

// The login an ID token belongs to, or null when it can't be read. Only
// compared locally; the API verifies every token it receives.
const tokenAuthTime = token => {
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const authTime = JSON.parse(atob(payload)).auth_time;
        return Number.isFinite(authTime) ? authTime : null;
    } catch {
        return null;
    }
};

const streamAuthorization = (token, ttlMs) => {
    try {
        // This only bounds local lifetime; the API still verifies the JWT.
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const {exp, auth_time: authTime} = JSON.parse(atob(payload));
        const expiresAt = Math.min(Date.now() + ttlMs, exp * 1000 - TOKEN_EXPIRY_SKEW_MS);
        if (!Number.isFinite(exp) || expiresAt <= Date.now()) throw new Error('Expired token');
        return {token, expiresAt, authTime};
    } catch {
        throw errorWithCode('Sign in to view this file', 'unauthenticated', 401);
    }
};

const responseError = async response => {
    let message = `File request failed (${response.status})`;
    try {
        const body = await response.clone().json();
        message = body?.error?.message || body?.error || message;
    } catch {
        // Byte endpoints normally return JSON only on failure.
    }
    const code = response.status === 401
        ? 'unauthenticated'
        : response.status === 403
            ? 'permission-denied'
            : 'download-failed';
    return errorWithCode(message, code, response.status);
};

const boundedBlob = async (response, maxBytes) => {
    const advertised = Number(response.headers.get('content-length'));
    if (Number.isFinite(advertised) && advertised > maxBytes) {
        await response.body?.cancel?.();
        throw errorWithCode('File is too large to preview in the browser', 'resource-exhausted');
    }

    if (!response.body?.getReader) {
        const blob = await response.blob();
        if (blob.size > maxBytes) {
            throw errorWithCode('File is too large to preview in the browser', 'resource-exhausted');
        }
        return safeBlob(blob);
    }

    const reader = response.body.getReader();
    const chunks = [];
    let size = 0;
    try {
        while (true) {
            const {done, value} = await reader.read();
            if (done) break;
            size += value.byteLength;
            if (size > maxBytes) {
                await reader.cancel();
                throw errorWithCode('File is too large to preview in the browser', 'resource-exhausted');
            }
            chunks.push(value);
        }
    } finally {
        reader.releaseLock?.();
    }
    // Object URLs run at the app's origin with none of the api's headers (#2068).
    return new Blob(chunks, {type: safeContentType(response.headers.get('content-type'))});
};

const randomChannel = () => {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const waitForController = async (registration, serviceWorkerUrl) => {
    const expected = new URL(serviceWorkerUrl, window.location.origin).href;
    const isProtectedWorker = worker => worker?.scriptURL === expected;
    if (isProtectedWorker(navigator.serviceWorker.controller)) return navigator.serviceWorker.controller;
    const worker = registration.active || registration.waiting || registration.installing;
    if (!worker) throw errorWithCode('Protected file transport is unavailable', 'transport-unavailable');

    return await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            navigator.serviceWorker.removeEventListener('controllerchange', changed);
            reject(errorWithCode('Protected file transport did not start', 'transport-unavailable'));
        }, 5000);
        const changed = () => {
            if (!isProtectedWorker(navigator.serviceWorker.controller)) return;
            clearTimeout(timeout);
            navigator.serviceWorker.removeEventListener('controllerchange', changed);
            resolve(navigator.serviceWorker.controller);
        };
        navigator.serviceWorker.addEventListener('controllerchange', changed);
        if (worker.state === 'activated' && isProtectedWorker(worker)) {
            worker.postMessage({type: 'EMBER_PROTECTED_FILES_CLAIM'});
        }
    });
};

const sendWorkerMessage = async (worker, message) => {
    const channel = new MessageChannel();
    const response = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(errorWithCode('Protected file transport did not respond', 'transport-unavailable')), 5000);
        channel.port1.onmessage = event => {
            clearTimeout(timeout);
            if (event.data?.ok) resolve();
            else reject(errorWithCode(event.data?.message || 'Protected file transport failed', 'transport-unavailable'));
        };
    });
    worker.postMessage(message, [channel.port2]);
    return await response;
};

const inferredRecordPath = file => {
    if (!file?.attachmentId || !file.filePath) return null;
    const suffix = `/${file.attachmentId}`;
    if (!file.filePath.endsWith(suffix)) return null;
    let path = file.filePath.slice(0, -suffix.length);
    if (file.grouping && path.endsWith(`/${file.grouping}`)) {
        path = path.slice(0, -(file.grouping.length + 1));
    }
    return path || null;
};

export const attachmentSelector = (file, recordPath, variant) => {
    const owner = recordPath || file?.recordPath || inferredRecordPath(file);
    if (!file?.attachmentId || !owner) return null;
    return {
        recordPath: owner,
        attachmentId: file.attachmentId,
        ...(variant && {variant}),
        revision: file.rotatedAt?.valueOf?.() || file.rotatedAt || file.updatedAt?.valueOf?.() || file.updatedAt || null
    };
};

export const createProtectedFileClient = options => {
    const {
        getToken,
        getApiBase,
        getRequestHeaders = () => ({}),
        maxEntryBytes = DEFAULT_ENTRY_BYTES,
        maxTotalBytes = DEFAULT_TOTAL_BYTES,
        maxEntries = DEFAULT_ENTRIES,
        ttlMs = DEFAULT_TTL_MS,
        streamTtlMs = 50 * 60 * 1000,
        serviceWorkerUrl = '/protected-file-sw.js',
        // Resolves once the session access lease is current (sessionAccess.js).
        whenAccessReady = whenAccessLeaseReady
    } = options;

    const entries = new Map();
    const allEntries = new Set();
    const listeners = new Set();
    let session = null;
    let generation = 0;
    let totalBytes = 0;
    let workerRegistration = null;
    let streamChannel = null;
    let sessionEpoch = 0;
    let streamQueue = Promise.resolve();
    // The login the cached previews were fetched under; undefined until known.
    let previewAuthTime;

    // Serialize token reads and worker messages so a slower old token cannot
    // overwrite a refresh. Session changes still revoke immediately.
    const enqueueStream = operation => {
        const pending = streamQueue.then(operation);
        streamQueue = pending.catch(() => {});
        return pending;
    };

    const emit = () => listeners.forEach(listener => listener(generation));

    const revokeEntry = entry => {
        if (entry.revoked) return;
        entry.revoked = true;
        clearTimeout(entry.expiryTimer);
        entry.controller?.abort();
        if (entry.url) URL.revokeObjectURL(entry.url);
        if (entry.size) totalBytes -= entry.size;
        allEntries.delete(entry);
    };

    const clearEntries = () => {
        for (const entry of allEntries) revokeEntry(entry);
        entries.clear();
        totalBytes = 0;
    };

    const revokeStreamChannel = () => {
        streamChannel?.worker.postMessage({
            type: 'EMBER_PROTECTED_FILES_REVOKE',
            channel: streamChannel.id
        });
        streamChannel = null;
    };

    const invalidatePreviews = () => {
        clearEntries();
        generation += 1;
        emit();
    };

    const invalidate = () => {
        sessionEpoch += 1;
        previewAuthTime = undefined;
        revokeStreamChannel();
        invalidatePreviews();
    };

    // Runs on window focus and on every ID-token refresh. Cached previews
    // survive both (#2088): wiping them re-downloaded every image on screen each
    // time the tab regained focus. They still expire after ttlMs, which bounds
    // how long a revoked grant stays visible, and a sign-out or account switch
    // still clears them through invalidate(). A new login by the same uid does
    // too, like the stream channel below.
    const refreshSession = () => {
        const channel = streamChannel;
        const epoch = sessionEpoch;
        return enqueueStream(async () => {
            let token;
            try {
                token = await getToken();
            } catch {
                if (channel && streamChannel === channel) revokeStreamChannel();
                return;
            }
            if (epoch !== sessionEpoch) return;
            const authTime = tokenAuthTime(token);
            if (previewAuthTime !== undefined && authTime !== previewAuthTime) {
                previewAuthTime = undefined;
                invalidatePreviews();
            }
            if (!channel || streamChannel !== channel) return;
            try {
                const authorization = streamAuthorization(token, streamTtlMs);
                const headers = await getRequestHeaders();
                if (epoch !== sessionEpoch || streamChannel !== channel) return;
                // A new login by the same uid is not a token refresh.
                if (authorization.authTime !== channel.authTime) {
                    revokeStreamChannel();
                    return;
                }
                await sendWorkerMessage(channel.worker, {
                    type: 'EMBER_PROTECTED_FILES_REFRESH',
                    channel: channel.id,
                    ...authorization,
                    headers
                });
            } catch {
                // Auth observers must not leak rejected promises or retain a
                // channel whose credentials could not be refreshed.
                if (streamChannel === channel) revokeStreamChannel();
            }
        });
    };

    const setSession = next => {
        if (next && next === session) return refreshSession();
        session = next || null;
        invalidate();
    };

    const apiTarget = selector => {
        const base = new URL(getApiBase());
        let target;
        if (selector?.url) {
            target = new URL(selector.url, base);
        } else {
            if (!selector?.recordPath || !selector?.attachmentId) {
                throw errorWithCode('File is missing its authenticated attachment identity', 'invalid-selector');
            }
            target = new URL('downloadAttachment', base);
            target.searchParams.set('recordPath', selector.recordPath);
            target.searchParams.set('attachmentId', selector.attachmentId);
            if (selector.variant) target.searchParams.set('variant', selector.variant);
        }
        if (target.origin !== base.origin) {
            throw errorWithCode('Protected file URL is outside the configured API', 'invalid-selector');
        }
        return target;
    };

    const selectorKey = selector => {
        try {
            return `${generation}:${apiTarget(selector)}:${selector?.revision || ''}`;
        } catch {
            return `${generation}:invalid`;
        }
    };

    const request = async (selector, {signal, headers} = {}) => {
        // The api refuses every read while the lease has lapsed.
        await whenAccessReady({signal});
        if (!session) throw errorWithCode('Sign in to view this file', 'unauthenticated', 401);
        const token = await getToken();
        if (!token) throw errorWithCode('Sign in to view this file', 'unauthenticated', 401);
        if (previewAuthTime === undefined) previewAuthTime = tokenAuthTime(token);
        const response = await fetch(apiTarget(selector), {
            signal,
            headers: {
                ...await getRequestHeaders(),
                ...headers,
                Authorization: `Bearer ${token}`
            }
        });
        if (!response.ok) throw await responseError(response);
        return response;
    };

    const evictUnused = requiredBytes => {
        const candidates = [...entries.entries()]
            .filter(([, entry]) => !entry.promise && entry.refs === 0)
            .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
        for (const [key, entry] of candidates) {
            if (entries.size <= maxEntries && totalBytes + requiredBytes <= maxTotalBytes) break;
            entries.delete(key);
            revokeEntry(entry);
        }
    };

    const acquire = async (selector, {entryBytes = maxEntryBytes} = {}) => {
        const key = selectorKey(selector);
        const now = Date.now();
        let entry = entries.get(key);
        if (entry && entry.expiresAt <= now) {
            entries.delete(key);
            if (entry.refs === 0) revokeEntry(entry);
            else entry.retired = true;
            entry = null;
        }

        if (!entry) {
            const controller = new AbortController();
            entry = {controller, refs: 0, lastUsed: now, size: 0, url: null, expiresAt: now + ttlMs};
            allEntries.add(entry);
            entry.promise = (async () => {
                const response = await request(selector, {signal: controller.signal});
                const blob = await boundedBlob(response, Math.min(entryBytes, maxEntryBytes));
                if (entries.get(key) !== entry) {
                    throw errorWithCode('File request was invalidated', 'aborted');
                }
                evictUnused(blob.size);
                if (entries.size > maxEntries || totalBytes + blob.size > maxTotalBytes) {
                    throw errorWithCode('Protected preview memory limit reached', 'resource-exhausted');
                }
                entry.url = URL.createObjectURL(blob);
                entry.size = blob.size;
                totalBytes += blob.size;
                entry.promise = null;
                entry.expiryTimer = setTimeout(() => {
                    if (entries.get(key) === entry) entries.delete(key);
                    entry.retired = true;
                    if (entry.refs === 0) revokeEntry(entry);
                }, Math.max(0, entry.expiresAt - Date.now()));
                return entry;
            })().catch(error => {
                if (entries.get(key) === entry) entries.delete(key);
                revokeEntry(entry);
                throw error;
            });
            entries.set(key, entry);
        }

        if (entry.promise) await entry.promise;
        entry.refs += 1;
        entry.lastUsed = Date.now();
        let released = false;
        return {
            url: entry.url,
            expiresAt: entry.expiresAt,
            release: () => {
                if (released) return;
                released = true;
                entry.refs = Math.max(0, entry.refs - 1);
                entry.lastUsed = Date.now();
                if (entry.refs === 0 && (entry.retired || entry.expiresAt <= Date.now())) {
                    if (entries.get(key) === entry) entries.delete(key);
                    revokeEntry(entry);
                }
            }
        };
    };

    const objectUrl = async (selector, options) => {
        const handle = await acquire(selector, options);
        handle.release();
        return handle.url;
    };

    const streamUrl = selector => {
        const epoch = sessionEpoch;
        return enqueueStream(async () => {
            if (!session) throw errorWithCode('Sign in to view this file', 'unauthenticated', 401);
            if (typeof navigator === 'undefined' || !navigator.serviceWorker || typeof MessageChannel === 'undefined') {
                throw errorWithCode('Streaming downloads are not supported by this browser', 'transport-unavailable');
            }
            if (!workerRegistration) workerRegistration = navigator.serviceWorker.register(serviceWorkerUrl, {scope: '/'});
            const registration = await workerRegistration;
            const worker = await waitForController(registration, serviceWorkerUrl);
            const authorization = streamAuthorization(await getToken(), streamTtlMs);
            const headers = await getRequestHeaders();
            if (epoch !== sessionEpoch) throw errorWithCode('File request was invalidated', 'aborted');
            if (streamChannel && (streamChannel.worker !== worker || streamChannel.authTime !== authorization.authTime)) {
                revokeStreamChannel();
            }
            if (!streamChannel) streamChannel = {id: randomChannel(), worker, authTime: authorization.authTime};
            const requestId = randomChannel();
            const target = apiTarget(selector);
            await sendWorkerMessage(worker, {
                type: 'EMBER_PROTECTED_FILES_AUTHORIZE',
                channel: streamChannel.id,
                requestId,
                ...authorization,
                origin: target.origin,
                target: target.toString(),
                headers
            });
            if (epoch !== sessionEpoch) throw errorWithCode('File request was invalidated', 'aborted');
            // The address bar receives only a random, short-lived lookup id. Record
            // paths, attachment ids, the API origin and credentials stay in worker
            // memory and never enter browser history or copied links.
            const url = new URL(`${STREAM_PATH}${encodeURIComponent(requestId)}`, window.location.origin);
            return url.toString();
        });
    };

    return {
        acquire,
        fetch: request,
        invalidate,
        keyFor: selectorKey,
        objectUrl,
        refreshSession,
        setSession,
        streamUrl,
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        _debug() {
            return {entries: entries.size, generation, session, totalBytes};
        }
    };
};

export const useProtectedFileUrl = (client, selector, options = {}) => {
    const [state, setState] = useState({url: null, loading: Boolean(selector), error: null});
    const [clientGeneration, setClientGeneration] = useState(0);
    const key = useMemo(() => client?.keyFor(selector), [client, selector]);

    useEffect(() => client?.subscribe(setClientGeneration), [client]);

    useEffect(() => {
        if (!client || !selector) {
            setState({url: null, loading: false, error: null});
            return undefined;
        }
        let active = true;
        let handle;
        let refreshTimer;
        setState({url: null, loading: true, error: null});
        client.acquire(selector, options).then(next => {
            if (!active) {
                next.release();
                return;
            }
            handle = next;
            setState({url: next.url, loading: false, error: null});
            refreshTimer = setTimeout(() => setClientGeneration(value => value + 1), Math.max(0, next.expiresAt - Date.now()));
        }).catch(error => {
            if (active) setState({url: null, loading: false, error});
        });
        return () => {
            active = false;
            clearTimeout(refreshTimer);
            handle?.release();
        };
    }, [client, key, clientGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

    return state;
};
